import React from 'react';
import SkillReport from '../SkillReport';
import LessonReport from '../LessonReport';
import StandardReport from '../StandardReport';
import { ReportType, DistrictReportVariantConfig } from './BaseDistrictReport';
import { Section } from './types';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const SECTION_REPORT_MAP: Record<
  ReportType,
  React.ComponentType<{
    data: {
      id: string | undefined;
      examId: number | undefined;
      sectionId: string;
      hideOutcomeSlider: boolean;
    };
    studentNameMap: Record<string, string>;
  }>
> = {
  skill: SkillReport,
  lesson: LessonReport,
  standard: StandardReport,
};

/**
 * Generates a district report configuration for the given report variant.
 *
 * The `getReportConfig` function returns a {@link DistrictReportVariantConfig} that drives
 * messaging (loading, error, empty states), item extraction from section data, and the
 * section-level report component. It uses the {@link ReportType} to:
 * - Resolve the section-level React component from `SECTION_REPORT_MAP` (e.g. 'skill' → SkillReport)
 * - Dynamically access the corresponding Section property (e.g. 'skill' → `section.skills`)
 *
 * @param reportType - The report variant. Acceptable values: `'skill'`, `'lesson'`, `'standard'`
 * @returns DistrictReportVariantConfig - Configuration for the district report variant
 */
export const getReportConfig = (reportType: ReportType): DistrictReportVariantConfig => ({
  reportType,
  loadingMessage: `Generating ${capitalize(reportType)} District Report...`,
  errorTitle: `Unable To Load ${capitalize(reportType)} Report`,
  emptyTitle: `No ${capitalize(reportType)} Data Available`,
  emptyDescription: `Students need to answer questions that cover those ${reportType}s on the exam before district reports can be generated.`,
  extractItems: (section: Section) =>
    section[`${reportType}s` as keyof Section] as
      | Array<{ students?: Array<{ student_id?: string; student_name?: string }> }>
      | undefined,
  SectionReport: SECTION_REPORT_MAP[reportType],
});
